'use client';

import { useEffect, useState } from 'react';
import { DestinationRFQModal } from './destination-rfq-modal';

interface ForwarderData {
  id: string;
  company_name: string;
  logo_url: string | null;
  country: string;
  ranking_score: number | null;
  avg_response_time_hours: number | null;
  avg_destination_rfq_response_hours: number | null;
  slug: string | null;
  email: string;
  forwarder_type: string | null;
}

interface DestinationCTAProps {
  destinationCountry: string | null;
  blReference: string;
  destinationPort?: string;
}

function countryCodeToFlag(code: string): string {
  return code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

function formatResponseHours(hours: number | null | undefined): string | null {
  if (!hours || hours <= 0) return null;
  if (hours < 1) return '< 1h response';
  if (hours < 24) return `~${Math.round(hours)}h response`;
  const days = Math.round(hours / 24);
  return `~${days}d response`;
}

export function DestinationCTA(props: DestinationCTAProps) {
  if (!props.destinationCountry) return null;
  return (
    <DestinationCountryCTA
      key={JSON.stringify([props.destinationCountry, props.blReference, props.destinationPort])}
      {...props}
      destinationCountry={props.destinationCountry}
    />
  );
}

function DestinationCountryCTA({
  destinationCountry,
  blReference,
  destinationPort,
}: DestinationCTAProps & { destinationCountry: string }) {
  const [forwarder, setForwarder] = useState<ForwarderData | null>(null);
  const [fallbackForwarder, setFallbackForwarder] = useState<ForwarderData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/forwarder-for-destination?country=${encodeURIComponent(destinationCountry)}`, {
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok) return;
        const data = await response.json();
        if (controller.signal.aborted) return;
        setForwarder(data.forwarder ?? null);
        setFallbackForwarder(data.fallbackForwarder ?? null);
      })
      .catch(() => { /* No recommendation is shown when the lookup fails. */ });
    return () => controller.abort();
  }, [destinationCountry]);

  const active = forwarder ?? fallbackForwarder;
  if (!active) return null;

  const label = forwarder
    ? 'LOCAL AGENT AT DESTINATION'
    : 'FEATURED FORWARDERS & VERIFIED PROFILES';

  const responseDisplay =
    formatResponseHours(active.avg_destination_rfq_response_hours) ??
    formatResponseHours(active.avg_response_time_hours);
  const flagEmoji = countryCodeToFlag(active.country);

  return (
    <>
      <div className="destination-cta">
        <div className="destination-cta-label">{label}</div>
        <div className="destination-cta-body">
          <div className="destination-cta-logo">
            {active.logo_url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={active.logo_url}
                alt={active.company_name}
                className="destination-cta-logo-img"
              />
            ) : (
              <div className="destination-cta-initials">
                {getInitials(active.company_name)}
              </div>
            )}
          </div>
          <div className="destination-cta-info">
            <div className="destination-cta-name">{active.company_name}</div>
            <div className="destination-cta-meta">
              <span className="destination-cta-flag">{flagEmoji}</span>
              <span className="destination-cta-country">{active.country}</span>
              {responseDisplay && (
                <span className="destination-cta-response">{responseDisplay}</span>
              )}
            </div>
          </div>
          <button className="destination-cta-btn" onClick={() => setModalOpen(true)}>
            Request Customs &amp; Delivery Services
          </button>
        </div>
      </div>

      <DestinationRFQModal
        forwarder={{ id: active.id, company_name: active.company_name }}
        blReference={blReference}
        destinationCountry={destinationCountry}
        destinationPort={destinationPort}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
